// src/hooks/useSimpleRouteProtection.ts
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalStorageString } from '@/hooks/use-local-storage';
import { AccountType } from '@/pages/auth/components/user-auth-form';

const getDashboardPath = (accountType: AccountType): string => {
  switch (accountType) {
    case AccountType.client:
    case AccountType.app_client:
      return '/client-portal/dashboard';

    case AccountType.business_team_leader:
    case AccountType.team_leader:
    case AccountType.staff_team_leader:
      return '/team-leader/dashboard';

    case AccountType.business_operations_managers:
    case AccountType.business_operations_manager:
    case AccountType.staff_operations_manager:
    case AccountType.operations_manager:
      return '/operations-manager/dashboard';

    case AccountType.business:
    case AccountType.business_admin:
    default:
      return '/';
  }
};

const hasAccess = (currentPath: string, accountType: AccountType): boolean => {
  // Business admin dashboard (root "/")
  if (currentPath === '/') {
    return accountType === AccountType.business || accountType === AccountType.business_admin;
  }
  
  if (currentPath.startsWith('/client-portal')) {
    return accountType === AccountType.client || accountType === AccountType.app_client;
  }
  
  if (currentPath.startsWith('/team-leader')) {
    return [
      AccountType.business_team_leader,
      AccountType.team_leader,
      AccountType.staff_team_leader
    ].includes(accountType);
  }
  
  if (currentPath.startsWith('/operations-manager')) {
    return [
      AccountType.business_operations_managers,
      AccountType.business_operations_manager,
      AccountType.staff_operations_manager,
      AccountType.operations_manager
    ].includes(accountType);
  }
  
  return true;
};

export const useSimpleRouteProtection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const accountType = useLocalStorageString('accountType') as AccountType;

  useEffect(() => {
    if (!accountType) return;
    
    const currentPath = location.pathname;
    
    if (!hasAccess(currentPath, accountType)) {
      const correctDashboard = getDashboardPath(accountType);
      navigate(correctDashboard, { replace: true });
    }
  }, [location.pathname, accountType, navigate]);
};